import { z } from 'zod';
import { StyleSchema } from './style';

export const IdSchema = z.string().min(1).max(64);
export type Id = string;

/** `core:<name>` for core types, `<moduleId>:<name>` for module types. */
export const TypeNameSchema = z.string().regex(/^[a-z0-9][a-z0-9.-]*:[a-z0-9][a-z0-9-]*$/);

const finite = z.number().finite();

/** A note the user left on an object (local: the canvas is the user's own). */
export const ObjectCommentSchema = z
  .object({
    id: IdSchema,
    text: z.string().min(1).max(4000),
    at: z.number(),
    done: z.boolean().optional(),
  })
  .passthrough();
export type ObjectComment = z.infer<typeof ObjectCommentSchema>;

/** Where a click on the object's link leads: a saved view of this canvas, another object, another canvas, a web address. */
export const ObjectLinkSchema = z
  .object({
    kind: z.enum(['bookmark', 'object', 'canvas', 'url']),
    target: z.string().min(1).max(2000),
  })
  .passthrough();
export type ObjectLink = z.infer<typeof ObjectLinkSchema>;

/** The root of a component master, or of one of its instances (the parts carry `componentPart`). */
export const ObjectComponentSchema = z
  .object({
    id: z.string().min(1).max(64),
    role: z.enum(['master', 'instance']),
    name: z.string().max(80).optional(),
  })
  .passthrough();
export type ObjectComponent = z.infer<typeof ObjectComponentSchema>;

/** Who added an object. A foundation for shared canvases: the id, and the name as it was. */
export const ObjectAuthorSchema = z
  .object({
    id: IdSchema,
    firstName: z.string().max(80),
    lastName: z.string().max(80).optional(),
  })
  .passthrough();
export type ObjectAuthor = z.infer<typeof ObjectAuthorSchema>;

export const ObjectRecordSchema = z
  .object({
    id: IdSchema,
    canvasId: IdSchema,
    type: TypeNameSchema,
    formatVersion: z.number().int().min(0),
    parentId: IdSchema.nullable(),
    /** Containing frame (frames are containers; groups use parentId). */
    frameId: IdSchema.nullable().optional(),
    z: z.string().min(1).max(256),
    x: finite,
    y: finite,
    w: finite.min(0),
    h: finite.min(0),
    rotation: finite,
    locked: z.boolean(),
    name: z.string().max(500).optional(),
    /** Resizing keeps the width-to-height ratio. */
    keepRatio: z.boolean().optional(),
    /** On a connector between shapes: the shape it leads to and all that hangs from it are folded away (a counter at its end). */
    collapsed: z.boolean().optional(),
    /** Notes on the object, oldest first. */
    comments: z.array(ObjectCommentSchema).max(500).optional(),
    /** Short tags (filterable in search). */
    labels: z.array(z.string().min(1).max(40)).max(20).optional(),
    /** Component master or instance root. */
    component: ObjectComponentSchema.optional(),
    /** In an instance: the master part this object mirrors. */
    componentPart: IdSchema.optional(),
    /** In an instance: what the user changed here (`style.<key>`, `props.<key>`, `name`, `geometry`), kept on sync. */
    overrides: z.array(z.string().max(64)).max(100).optional(),
    /** A link to follow from the object. */
    link: ObjectLinkSchema.optional(),
    /** Profile that added the object. Older objects omit it. */
    addedBy: ObjectAuthorSchema.optional(),
    /** API 4.0: the module that created this object; dropped by the first edit of the user (the object stops being the module's own). */
    createdByModule: z.string().min(1).max(128).optional(),
    /** Emoji put on the object. */
    reactions: z.array(z.string().min(1).max(16)).max(20).optional(),
    style: StyleSchema,
    props: z.record(z.unknown()),
  })
  .passthrough();

/** Unknown fields are preserved verbatim (§7.5). */
export type ObjectRecord = z.infer<typeof ObjectRecordSchema>;

export const LinkRecordSchema = z
  .object({
    id: IdSchema,
    canvasId: IdSchema,
    type: TypeNameSchema,
    from: IdSchema,
    to: IdSchema,
    directed: z.boolean(),
    formatVersion: z.number().int().min(0),
    props: z.record(z.unknown()),
  })
  .passthrough();
export type LinkRecord = z.infer<typeof LinkRecordSchema>;

export const CanvasMetaSchema = z
  .object({
    id: IdSchema,
    name: z.string().max(500),
    createdAt: z.number(),
    updatedAt: z.number(),
    formatVersion: z.string(),
    lastExportAt: z.number().nullable(),
    objectCount: z.number().int().min(0),
    settings: z.record(z.unknown()).optional(),
  })
  .passthrough();
export type CanvasMeta = z.infer<typeof CanvasMetaSchema>;

export type TableName = 'objects' | 'links';
export type AnyRecord = ObjectRecord | LinkRecord;

/** Core object type names. */
export const CoreTypes = {
  rect: 'core:rect',
  ellipse: 'core:ellipse',
  triangle: 'core:triangle',
  diamond: 'core:diamond',
  text: 'core:text',
  sticker: 'core:sticker',
  frame: 'core:frame',
  image: 'core:image',
  line: 'core:line',
  connector: 'core:connector',
  pen: 'core:pen',
  /** Bezier path drawn with the pen tool (nodes with handles, open or closed). */
  path: 'core:path',
  /** Live DSkhema diagram: text source, laid out automatically (docs/ru/DSKHEMA.md). */
  diagram: 'core:diagram',
  group: 'core:group',
} as const;
export type CoreTypeName = (typeof CoreTypes)[keyof typeof CoreTypes];

export function isCoreType(type: string): boolean {
  return type.startsWith('core:');
}

/** Module id that owns a type (`null` for core). */
export function typeOwner(type: string): string | null {
  const i = type.lastIndexOf(':');
  const owner = type.slice(0, i);
  return owner === 'core' ? null : owner;
}

export interface ConnectorEnd {
  objectId?: Id;
  /** Normalized anchor inside the target's box (0..1), or undefined for automatic. */
  anchor?: { x: number; y: number };
  /** Position along a target connector/line path (0..1) — arrows pointing at other connectors. */
  t?: number;
  /** World point, used when not attached. */
  point: { x: number; y: number };
}
